import { i18n } from '~/features/internationalization/internationalization';
import type { Currency } from '~/features/internationalization/models/currency';

import type { DynamicPrice } from '../models/dynamic-price';

const PROGRAM_MONTHS = 19;

export function getMinorUnitDivisor(currency: Currency): number {
  const fractionDigits =
    new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().minimumFractionDigits ?? 2;
  return 10 ** fractionDigits;
}

export function computeRoundedTotal(monthlyAmount: number, enrollmentFee: number, currency: Currency = 'USD'): number {
  const divisor = getMinorUnitDivisor(currency);
  const totalMinorUnits = PROGRAM_MONTHS * monthlyAmount + enrollmentFee;
  const totalMajorUnits = totalMinorUnits / divisor;
  const roundTo = divisor === 1 ? 10000 : 100;
  return Math.ceil(totalMajorUnits / roundTo) * roundTo;
}

export function formatWholePrice(amount: number, currency: Currency): string {
  return new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
}

export function computeDynamicPriceResult(data: DynamicPrice) {
  const currency = data.currency as Currency;
  const divisor = getMinorUnitDivisor(currency);
  const discountedTotal = computeRoundedTotal(data.monthlyPayableAmount, data.enrollmentFee, currency);
  const maxTotal = computeRoundedTotal(data.monthlyOriginalAmount, data.enrollmentFee, currency);
  const isDiscounted = data.isForeverCoupon && discountedTotal < maxTotal;
  const effectiveTotal = isDiscounted ? discountedTotal : maxTotal;

  return {
    total: effectiveTotal,
    maxTotal,
    formattedTotal: formatWholePrice(effectiveTotal, currency),
    formattedEnrollmentFee: formatWholePrice(data.enrollmentFee / divisor, currency),
    isDiscounted,
    currency: data.currency,
    percentOff: data.isForeverCoupon ? data.percentOff : null,
  };
}
